"use client";

import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Trash2, Loader2, CheckCircle2, AlertCircle, FileCheck, UserPlus, LayoutDashboard, FileText, Menu, Upload } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { SharePointFileUpload } from "../ui/sharepoint-file-upload";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";
import { RenameFileDialog } from "./RenameFileDialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/HoverCard";

type SharePointDoc = {
  name: string;
  url: string;
  reference?: string;
};

type SignoffEntry = {
  name: string;
  prep_so: Array<{ name: string; metadata: string }>;
  rev_so: Array<{ name: string; metadata: string }>;
  conc_so: Array<{ name: string; metadata: string }>;
  eqr_so: Array<{ name: string; metadata: string }>;
  partner_so: Array<{ name: string; metadata: string }>;
};

type SignoffData = {
  signoff: SignoffEntry[];
};

type AssignDocumentData = {
  data: {
    [fileName: string]: string[];
  };
};

type TeamMember = {
  id: string;
  user_name?: string;
  email?: string;
};

// Helper function to get initials from a name
const getInitials = (name: string): string => {
  if (!name || name.trim() === "") return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const formatSignoffDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch {
    return dateString;
  }
};

const StaticSignoffGroup = ({
  label,
  entries,
}: {
  label: string;
  entries: Array<{ name: string; metadata: string }>;
}) => {
  const validEntries = entries.filter((entry) => entry.name && entry.name.trim() !== "");
  if (validEntries.length === 0) return null;

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-0.5 cursor-help">
          {validEntries.slice(0, 3).map((entry, i) => (
            <div
              key={i}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
              title={`${entry.name} - ${formatSignoffDate(entry.metadata)}`}
            >
              {getInitials(entry.name)}
            </div>
          ))}
          {validEntries.length > 3 && (
            <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
              +{validEntries.length - 3}
            </div>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-3">
        <div className="font-semibold mb-1 text-white/90">{label} ({validEntries.length})</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {validEntries.map((entry, idx) => (
            <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
              <div className="font-medium text-xs text-white/90">{entry.name}</div>
              {entry.metadata && (
                <div className="text-white/70 text-[10px]">{formatSignoffDate(entry.metadata)}</div>
              )}
            </div>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

// Component to display signoff badge with hover tooltip and signoff button
const SignoffBadge = ({ 
  label, 
  signoffArray, 
  isEmpty,
  onSignoffClick,
  disabled = false,
  section
}: { 
  label: string; 
  signoffArray: Array<{ name: string; metadata: string }>; 
  isEmpty: boolean;
  onSignoffClick?: () => void;
  disabled?: boolean;
  section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so';
}) => {
  // Filter out empty entries
  const validEntries = signoffArray.filter(entry => entry.name && entry.name.trim() !== "");
  const hasEntries = validEntries.length > 0;
  
  return (
    <div className="relative inline-flex items-center gap-1">
      {/* Display signoff entries */}
      {hasEntries && (
        <HoverCard openDelay={150} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div className="flex flex-col items-center gap-1 cursor-help">
              {validEntries.slice(0, 3).map((entry, idx) => {
                const initials = getInitials(entry.name);
                return (
                  <div
                    key={idx}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                    title={`${entry.name} - ${formatDate(entry.metadata)}`}
                  >
                    {initials}
                  </div>
                );
              })}
              {validEntries.length > 3 && (
                <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30">
                  +{validEntries.length - 3}
                </div>
              )}
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-72 p-3">
            <div className="font-semibold mb-1 text-white/90">{label} ({validEntries.length})</div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {validEntries.map((entry, idx) => (
                <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                  <div className="font-medium text-xs text-white/90">{entry.name}</div>
                  {entry.metadata && (
                    <div className="text-white/70 text-[10px]">{formatSignoffDate(entry.metadata)}</div>
                  )}
                </div>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
      
      {/* Always show signoff button */}
      <Button
        size="sm"
        variant="ghost"
        onClick={onSignoffClick}
        disabled={disabled}
        className={`h-6 w-6 p-0 ${disabled ? 'text-white/30 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'}`}
        title={disabled ? `You don't have permission to add ${label} Signoff` : `Add ${label} Signoff`}
      >
        <FileCheck className="h-3 w-3" />
      </Button>
      
    </div>
  );
};

// Component to display assign document button with hover tooltip
const AssignDocumentBadge = ({
  assignedUsers,
  disabled = false,
  loading = false,
  children,
}: {
  assignedUsers: string[];
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const hasAssignedUsers = assignedUsers.length > 0;

  return (
    <div 
      className="relative inline-flex items-center"
      onMouseEnter={() => hasAssignedUsers && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      
      {/* Tooltip showing assigned users */}
      {showTooltip && hasAssignedUsers && (
        <div className="absolute z-10 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg border border-white/10 bottom-full mb-2 left-1/2 transform -translate-x-1/2 min-w-[180px] max-w-md">
          <div className="font-semibold mb-1">Assigned Users ({assignedUsers.length})</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {assignedUsers.map((userName, idx) => (
              <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                <div className="font-medium">{userName}</div>
              </div>
            ))}
          </div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface DocumentListWithActionsProps {
  documents: SharePointDoc[];
  loading?: boolean;
  error?: string;
  onRefresh?: () => void;
  title?: string;
  showUploadButton?: boolean;
  onUploadClick?: () => void;
  uploadSectionList?: string[];
  uploadFolderName?: string;
  uploadMultiple?: boolean;
  /** If provided, and this workspace exists in Confirmation_Response.json (partydetails.area), show "Go to Confirmation Dashboard" above the list */
  workspaceName?: string;
  /** Called when user clicks "Go to Confirmation Dashboard"; receives workspace name so caller can navigate with workspace filter applied */
  onNavigateToConfirmationLibrary?: (workspace: string) => void;
  /** Called when user clicks "Go to Sampling Dashboard"; receives workspace name so caller can navigate with workspace filter applied */
  onNavigateToSamplingLibrary?: (workspace: string) => void;
}

export default function DocumentListWithActions({
  documents,
  loading = false,
  error = "",
  onRefresh,
  title = "Documents",
  showUploadButton = false,
  onUploadClick,
  uploadSectionList = [],
  uploadFolderName = "client",
  uploadMultiple = true,
  workspaceName,
  onNavigateToConfirmationLibrary,
  onNavigateToSamplingLibrary,
}: DocumentListWithActionsProps) {
  const [showUploader, setShowUploader] = useState<boolean>(false);
  const [showConfirmationDashboardLink, setShowConfirmationDashboardLink] = useState<boolean>(false);
  const [showSamplingDashboardLink, setShowSamplingDashboardLink] = useState<boolean>(false);
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [signoffData, setSignoffData] = useState<SignoffData | null>(null);
  const [loadingSignoff, setLoadingSignoff] = useState<boolean>(false);
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState<boolean>(false);
  const [assignDocumentData, setAssignDocumentData] = useState<AssignDocumentData | null>(null);
  const [loadingAssignDocument, setLoadingAssignDocument] = useState<boolean>(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [selectedDocNames, setSelectedDocNames] = useState<Set<string>>(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [fileToRename, setFileToRename] = useState<{ name: string; area: string } | null>(null);
  const [replacingFile, setReplacingFile] = useState<{ name: string; area: string } | null>(null);
  const [fileOptionsDropdownOpen, setFileOptionsDropdownOpen] = useState<Record<string, boolean>>({}); // Control dropdown state per file

  const toggleDocSelection = (name: string) => {
    setSelectedDocNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAllDocs = () => {
    const names = documents.filter((d, i, self) => self.findIndex((x) => x.name === d.name) === i).map((d) => d.name);
    const allSelected = names.length > 0 && names.every((n) => selectedDocNames.has(n));
    setSelectedDocNames(allSelected ? new Set() : new Set(names));
  };

  const loadSignoffData = async () => {
    try {
      setLoadingSignoff(true);
      const res = await (window as any)?.sharePointAPI?.downloadSignoffJson?.();
      if (res?.success) {
        setSignoffData(res.data || { signoff: [] });
      } else {
        setSignoffData({ signoff: [] });
      }
    } catch (e: any) {
      console.error("Error loading signoff data:", e);
      setSignoffData({ signoff: [] });
    } finally {
      setLoadingSignoff(false);
    }
  };

  const loadAssignDocumentData = async () => {
    try {
      setLoadingAssignDocument(true);
      const res = await (window as any)?.sharePointAPI?.downloadAssignDocumentJson?.();
      if (res?.success) {
        setAssignDocumentData(res.data || { data: {} });
      } else {
        setAssignDocumentData({ data: {} });
      }
    } catch (e: any) {
      console.error("Error loading assign document data:", e);
      setAssignDocumentData({ data: {} });
    } finally {
      setLoadingAssignDocument(false);
    }
  };

  const loadTeamMembers = async () => {
    if (!currentEngagement) {
      setTeamMembers([]);
      return;
    }

    try {
      setLoadingTeamMembers(true);
      const team = await apiService.getEngagementTeam(currentEngagement.id);
      const normalizedTeam = team.map((member: any) => ({
        id: member.id,
        user_name: member.user_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
      }));
      setTeamMembers(normalizedTeam);
    } catch (error) {
      console.error('Error loading team members:', error);
      setTeamMembers([]);
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const loadUserEngagementRole = async () => {
    if (!currentEngagement || !user) {
      setUserEngagementRole(null);
      return;
    }

    try {
      setLoadingRole(true);
      const team = await apiService.getEngagementTeam(currentEngagement.id);
      const currentUserMember = team.find((member: any) => member.id === user.id);
      const role = currentUserMember?.role || null;
      setUserEngagementRole(role);
    } catch (error) {
      console.error("Error loading user engagement role:", error);
      setUserEngagementRole(null);
    } finally {
      setLoadingRole(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      await Promise.all([loadSignoffData(), loadAssignDocumentData()]);
      await loadUserEngagementRole();
      await loadTeamMembers();
    };
    run();
    return () => {
      mounted = false;
    };
  }, [currentEngagement, user]);

  // Check if workspace is in Confirmation_Response.json (partydetails.area) to show "Go to Confirmation Dashboard"
  // and if it is in Sampling_SamplingLog.json (top-level keys) to show "Go to Sampling Dashboard".
  // Depend ONLY on workspaceName so the flags are stable and don't flicker when parent re-renders.
  useEffect(() => {
    if (!workspaceName?.trim()) {
      setShowConfirmationDashboardLink(false);
      setShowSamplingDashboardLink(false);
      return;
    }

    const trimmedName = String(workspaceName).trim();

    (async () => {
      try {
        const res = await (window as any)?.sharePointAPI?.downloadClientJson?.("Confirmation_Response.json");
        if (!res?.success || typeof res.data !== "object") {
          setShowConfirmationDashboardLink(false);
          return;
        }
        const areas = new Set<string>(
          Object.values(res.data)
            .map((d: any) => d?.partydetails?.area)
            .filter((a: any) => a != null && String(a).trim() !== "")
        );
        setShowConfirmationDashboardLink(areas.has(trimmedName));
      } catch {
        setShowConfirmationDashboardLink(false);
      }
    })();

    (async () => {
      try {
        const res = await (window as any)?.sharePointAPI?.downloadClientJson?.("Sampling_SamplingLog.json");
        if (!res?.success || typeof res.data !== "object") {
          setShowSamplingDashboardLink(false);
          return;
        }
        const workspaces = new Set<string>(Object.keys(res.data));
        setShowSamplingDashboardLink(workspaces.has(trimmedName));
      } catch {
        setShowSamplingDashboardLink(false);
      }
    })();
  }, [workspaceName]);

  // Get signoff entry for a specific file
  const getSignoffForFile = (fileName: string): SignoffEntry | null => {
    if (!signoffData || !signoffData.signoff) return null;
    return signoffData.signoff.find((entry) => entry.name === fileName) || null;
  };

  // Get assigned users for a specific file
  const getAssignedUsersForFile = (fileName: string): string[] => {
    if (!assignDocumentData || !assignDocumentData.data) return [];
    return assignDocumentData.data[fileName] || [];
  };

  // Map roles to signoff sections
  const canSignoffSection = (section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so'): boolean => {
    if (!user) return false;

    const engagementRole = userEngagementRole;
    const userType = user.type;

    switch (section) {
      case 'prep_so':
        return (engagementRole === 'associate' || engagementRole === 'article') ||
               (userType === 'associate' || userType === 'article');
      case 'rev_so':
        return engagementRole === 'engagement_manager' ||
               userType === 'manager' ||
               userType === 'associate' ||
               engagementRole === 'associate';
      case 'conc_so':
        return userType === 'manager' || userType === 'partner';
      case 'eqr_so':
        return engagementRole === 'eqr_partner' || engagementRole === 'eqr_manager';
      case 'partner_so':
        return engagementRole === 'engagement_partner';
      default:
        return false;
    }
  };

  // Check if prep_so is completed for a file
  const isPrepSoCompleted = (signoffEntry: SignoffEntry | null): boolean => {
    if (!signoffEntry) return false;
    return signoffEntry.prep_so && 
           signoffEntry.prep_so.some(entry => entry.name && entry.name.trim() !== "");
  };

  const handleSignoff = async (fileName: string, section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so') => {
    if (!user || !user.user_name) {
      return;
    }

    try {
      setLoadingSignoff(true);

      const result = await (window as any)?.sharePointAPI?.uploadSignoff?.({
        fileName: fileName,
        section: section,
        userName: user.user_name
      });

      if (result?.success) {
        await loadSignoffData();
        setUploadStatus({
          success: true,
          message: `Signoff updated successfully for ${fileName}`
        });
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        setUploadStatus({
          success: false,
          message: result?.error || "Failed to update signoff"
        });
      }
    } catch (e: any) {
      const errorMessage = e?.message || "Unexpected error while updating signoff";
      setUploadStatus({
        success: false,
        message: errorMessage
      });
    } finally {
      setLoadingSignoff(false);
    }
  };

  const handleBulkSignoff = async (section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so') => {
    if (!user?.user_name || selectedDocNames.size === 0) return;
    const names = Array.from(selectedDocNames);
    try {
      setLoadingSignoff(true);
      let successCount = 0;
      for (const fileName of names) {
        const result = await (window as any)?.sharePointAPI?.uploadSignoff?.({
          fileName,
          section,
          userName: user.user_name,
        });
        if (result?.success) successCount++;
      }
      await loadSignoffData();
      setSelectedDocNames(new Set());
      if (onRefresh) onRefresh();
    } finally {
      setLoadingSignoff(false);
    }
  };

  const handleBulkAssignDocument = async (userName: string) => {
    if (!userName.trim() || selectedDocNames.size === 0) return;
    const names = Array.from(selectedDocNames);
    try {
      setLoadingAssignDocument(true);
      for (const fileName of names) {
        await (window as any)?.sharePointAPI?.uploadAssignDocument?.({
          fileName,
          userName: userName.trim(),
        });
      }
      await loadAssignDocumentData();
      setSelectedDocNames(new Set());
      if (onRefresh) onRefresh();
    } finally {
      setLoadingAssignDocument(false);
    }
  };

  const handleAssignDocument = async (fileName: string, userName: string) => {
    if (!fileName || !userName) {
      return;
    }

    try {
      setLoadingAssignDocument(true);

      const result = await (window as any)?.sharePointAPI?.uploadAssignDocument?.({
        fileName: fileName,
        userName: userName
      });

      if (result?.success) {
        await loadAssignDocumentData();
        setUploadStatus({
          success: true,
          message: `Document assigned successfully to ${userName}`
        });
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        setUploadStatus({
          success: false,
          message: result?.error || "Failed to assign document"
        });
      }
    } catch (e: any) {
      const errorMessage = e?.message || "Unexpected error while assigning document";
      setUploadStatus({
        success: false,
        message: errorMessage
      });
    } finally {
      setLoadingAssignDocument(false);
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!fileName) {
      return;
    }

    if (!confirm(`Are you sure you want to delete "${fileName}"? This will move it to the recycle bin.`)) {
      return;
    }

    try {
      setLoadingSignoff(true);
      const result = await (window as any)?.sharePointAPI?.deleteDocumentLibrary?.(fileName);
      
      if (result?.success) {
        // Also remove the file entry from AssignDocument.json
        try {
          const removeResult = await (window as any)?.sharePointAPI?.removeAssignDocument?.(fileName);
          if (removeResult?.success) {
            console.log("Removed file entry from AssignDocument.json", { fileName });
          } else {
            console.warn("Failed to remove file entry from AssignDocument.json:", removeResult?.error);
          }
        } catch (removeError) {
          console.warn("Error removing file entry from AssignDocument.json:", removeError);
        }

        setUploadStatus({
          success: true,
          message: result.message || `Successfully deleted ${fileName}`
        });
        await Promise.all([loadSignoffData(), loadAssignDocumentData()]);
        if (onRefresh) {
          onRefresh();
        }
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        setUploadStatus({
          success: false,
          message: result?.error || "Failed to delete file"
        });
      }
    } catch (e: any) {
      setUploadStatus({
        success: false,
        message: e?.message || "Unexpected error during deletion"
      });
    } finally {
      setLoadingSignoff(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDocNames.size === 0) return;
    const count = selectedDocNames.size;
    const names = Array.from(selectedDocNames);
    if (!confirm(`Are you sure you want to delete ${count} selected file(s)? This will move them to the recycle bin.`)) {
      return;
    }
    setDeletingSelected(true);
    let successCount = 0;
    let failCount = 0;
    try {
      for (const fileName of names) {
        try {
          const result = await (window as any)?.sharePointAPI?.deleteDocumentLibrary?.(fileName);
          if (result?.success) {
            successCount++;
            try {
              await (window as any)?.sharePointAPI?.removeAssignDocument?.(fileName);
            } catch {
              // ignore
            }
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }
      setSelectedDocNames(new Set());
      await Promise.all([loadSignoffData(), loadAssignDocumentData()]);
      if (onRefresh) onRefresh();
      if (failCount === 0) {
        setUploadStatus({ success: true, message: `Successfully deleted ${successCount} file(s).` });
      } else {
        setUploadStatus({ success: false, message: `Deleted ${successCount} file(s). ${failCount} failed.` });
      }
      setTimeout(() => setUploadStatus(null), 3000);
    } catch (e: any) {
      setUploadStatus({ success: false, message: e?.message || "Error during deletion" });
      setTimeout(() => setUploadStatus(null), 3000);
    } finally {
      setDeletingSelected(false);
    }
  };

  const handleFileClick = async (url: string) => {
    if (url) {
      // Use Electron's shell.openExternal to open in default browser
      if ((window as any).electronWindow?.openExternal) {
        try {
          await (window as any).electronWindow.openExternal(url);
        } catch (error) {
          console.error("Error opening external URL:", error);
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }
  };

  const handleRenameClick = async (fileName: string) => {
    // Close dropdown first
    setFileOptionsDropdownOpen(prev => ({ ...prev, [fileName]: false }));
    
    // Small delay to ensure dropdown closes before dialog opens
    setTimeout(() => {
      // Use uploadFolderName as the area, default to "client" if not provided
      const area = uploadFolderName || "client";
      setFileToRename({ name: fileName, area });
      setRenameDialogOpen(true);
    }, 100);
  };

  const handleRename = async (newName: string) => {
    if (!fileToRename) return;

    try {
      setLoadingSignoff(true);
      const result = await (window as any)?.sharePointAPI?.renameFile?.({
        oldName: fileToRename.name,
        newName: newName,
        area: fileToRename.area,
      });

      if (result?.success) {
        setUploadStatus({
          success: true,
          message: result.message || `Successfully renamed ${fileToRename.name} to ${result.data?.new_name}`
        });
        // Refresh the document list
        if (onRefresh) {
          await onRefresh();
        }
        await Promise.all([loadSignoffData(), loadAssignDocumentData()]);
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        throw new Error(result?.error || "Failed to rename file");
      }
    } catch (e: any) {
      setUploadStatus({
        success: false,
        message: e?.message || "Unexpected error during rename"
      });
      setTimeout(() => {
        setUploadStatus(null);
      }, 3000);
      throw e; // Re-throw so dialog can handle it
    } finally {
      setLoadingSignoff(false);
    }
  };

  const handleReplaceClick = async (fileName: string) => {
    // Close dropdown first
    setFileOptionsDropdownOpen(prev => ({ ...prev, [fileName]: false }));
    
    const area = uploadFolderName || "client";
    setReplacingFile({ name: fileName, area });
    
    try {
      // Open file picker
      const result = await (window as any)?.sharePointAPI?.openFileDialog?.();
      
      if (!result?.success || !result?.filePath) {
        setReplacingFile(null);
        return;
      }

      const filePath = result.filePath;
      
      // Replace the file
      setLoadingSignoff(true);
      const replaceResult = await (window as any)?.sharePointAPI?.replaceFile?.({
        filePath: filePath,
        fileName: fileName,
        area: area,
      });

      if (replaceResult?.success) {
        setUploadStatus({
          success: true,
          message: replaceResult.message || `Successfully replaced ${fileName}`
        });
        // Refresh the document list
        if (onRefresh) {
          await onRefresh();
        }
        await Promise.all([loadSignoffData(), loadAssignDocumentData()]);
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      } else {
        setUploadStatus({
          success: false,
          message: replaceResult?.error || "Failed to replace file"
        });
        setTimeout(() => {
          setUploadStatus(null);
        }, 3000);
      }
    } catch (e: any) {
      setUploadStatus({
        success: false,
        message: e?.message || "Unexpected error during replace"
      });
      setTimeout(() => {
        setUploadStatus(null);
      }, 3000);
    } finally {
      setLoadingSignoff(false);
      setReplacingFile(null);
    }
  };

  // Deduplicate documents by name
  const uniqueDocs = documents.filter((doc, index, self) => 
    index === self.findIndex((d) => d.name === doc.name)
  );

  const handleUploadClick = () => {
    setShowUploader((v) => !v);
    if (onUploadClick) {
      onUploadClick();
    }
  };

  const handleFilesUploaded = async () => {
    if (onRefresh) {
      await onRefresh();
    }
    setShowUploader(false);
  };

  return (
    <div className="space-y-4">
      {(showConfirmationDashboardLink || showSamplingDashboardLink) && workspaceName && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {showConfirmationDashboardLink && onNavigateToConfirmationLibrary && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-white/20 text-white/90 hover:bg-white/10 hover:text-white"
              onClick={() => onNavigateToConfirmationLibrary(workspaceName)}
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Go to Confirmation Dashboard
            </Button>
          )}
          {showSamplingDashboardLink && onNavigateToSamplingLibrary && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-white/20 text-white/90 hover:bg-white/10 hover:text-white"
              onClick={() => onNavigateToSamplingLibrary(workspaceName)}
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Go to Sampling Dashboard
            </Button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">{title}</h3>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {uniqueDocs.length} document(s)
          </Badge>
          {showUploadButton && !isArchivalMode && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleUploadClick}
              className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
            >
              Upload Files
            </Button>
          )}
          {!isArchivalMode && selectedDocNames.size > 0 && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={loadingSignoff}
                    className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                  >
                    {loadingSignoff ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileCheck className="h-4 w-4 mr-2" />
                    )}
                    Sign off on selected ({selectedDocNames.size})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                  {(() => {
                    const selected = Array.from(selectedDocNames);
                    const allHavePrep = selected.length > 0 && selected.every((name) => isPrepSoCompleted(getSignoffForFile(name)));
                    const canPrep = canSignoffSection("prep_so");
                    const canRev = canSignoffSection("rev_so") && allHavePrep;
                    const canConc = canSignoffSection("conc_so") && allHavePrep;
                    const canEqr = canSignoffSection("eqr_so") && allHavePrep;
                    const canPartner = canSignoffSection("partner_so") && allHavePrep;
                    return (
                      <>
                        <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkSignoff("prep_so")}>Preparer</DropdownMenuItem>
                        <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkSignoff("rev_so")}>Reviewer</DropdownMenuItem>
                        <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkSignoff("conc_so")}>Concurrent</DropdownMenuItem>
                        <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkSignoff("eqr_so")}>EQR</DropdownMenuItem>
                        <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkSignoff("partner_so")}>Partner</DropdownMenuItem>
                      </>
                    );
                  })()}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={loadingAssignDocument || teamMembers.length === 0}
                    className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    {loadingAssignDocument ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4 mr-2" />
                    )}
                    Assign selected ({selectedDocNames.size})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px] max-h-64 overflow-y-auto" align="end">
                  {loadingTeamMembers ? (
                    <div className="p-2 text-center text-white/60 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div className="p-2 text-center text-white/60 text-sm">No team members available</div>
                  ) : (
                    teamMembers.map((member) => (
                      <DropdownMenuItem
                        key={member.id}
                        className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer"
                        onClick={() => handleBulkAssignDocument(member.user_name || member.email || "")}
                        disabled={loadingAssignDocument}
                      >
                        <div className="flex flex-col">
                          <span>{member.user_name || member.email}</span>
                          {member.email && member.user_name && (
                            <span className="text-xs text-white/60">{member.email}</span>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                size="sm"
                variant="outline"
                disabled={deletingSelected}
                onClick={handleDeleteSelected}
                className="border-red-500/50 text-red-300 hover:bg-red-500/10 hover:text-red-200"
              >
                {deletingSelected ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete selected ({selectedDocNames.size})
              </Button>
            </>
          )}
        </div>
      </div>

      {showUploadButton && showUploader && !isArchivalMode && uploadSectionList.length > 0 && (
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-4">
            <SharePointFileUpload
              onFilesUploaded={handleFilesUploaded}
              multiple={uploadMultiple}
              accept="*"
              triggerText="Select File"
              showReferenceInput={true}
              referencePlaceholder="Enter reference"
              sectionList={uploadSectionList}
              sectionToFind=""
              folderName={uploadFolderName}
              linkFromSharePointMultiple={true}
            />
          </CardContent>
        </Card>
      )}

      {uploadStatus && (
        <div className={`p-3 rounded-md border ${
          uploadStatus.success
            ? "border-green-500/20 bg-green-500/10 text-green-300"
            : "border-red-500/20 bg-red-500/10 text-red-300"
        }`}>
          <div className="flex items-center gap-2">
            {uploadStatus.success ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <span className="text-sm">{uploadStatus.message}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-white/60 py-8">Loading...</div>
      ) : error ? (
        <div className="text-sm text-red-400">{error}</div>
      ) : uniqueDocs.length === 0 ? (
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <FileText className="mx-auto h-12 w-12 mb-4 text-white/40" />
              <p>No documents found.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {!loadingSignoff && (
            <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
              {!isArchivalMode && (
                <div className="w-10 shrink-0 flex items-center justify-center">
                  <Checkbox
                    checked={uniqueDocs.length > 0 && uniqueDocs.every((d) => selectedDocNames.has(d.name))}
                    onCheckedChange={selectAllDocs}
                    aria-label="Select all documents"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">Document</div>
              <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
              <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
              <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
              <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
              <div className="w-24 shrink-0 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
              <div className="w-20 shrink-0" />
            </div>
          )}
          <div className="space-y-2">
            {uniqueDocs.map((d, idx) => {
                  const signoffEntry = getSignoffForFile(d.name);
                  const hasPrepSo = signoffEntry && signoffEntry.prep_so && 
                                    signoffEntry.prep_so.some(entry => entry.name && entry.name.trim() !== "");
                  const hasRevSo = signoffEntry && signoffEntry.rev_so && 
                                   signoffEntry.rev_so.some(entry => entry.name && entry.name.trim() !== "");
                  const hasConcSo = signoffEntry && signoffEntry.conc_so && 
                                    signoffEntry.conc_so.some(entry => entry.name && entry.name.trim() !== "");
                  const hasEqrSo = signoffEntry && signoffEntry.eqr_so && 
                                   signoffEntry.eqr_so.some(entry => entry.name && entry.name.trim() !== "");
                  const hasPartnerSo = signoffEntry && signoffEntry.partner_so && 
                                       signoffEntry.partner_so.some(entry => entry.name && entry.name.trim() !== "");
                  
                  const prepSoCompleted = isPrepSoCompleted(signoffEntry);
                  
                  const canSignoffPrep = canSignoffSection('prep_so');
                  const canSignoffRev = canSignoffSection('rev_so') && prepSoCompleted;
                  const canSignoffConc = canSignoffSection('conc_so') && prepSoCompleted;
                  const canSignoffEqr = canSignoffSection('eqr_so') && prepSoCompleted;
                  const canSignoffPartner = canSignoffSection('partner_so') && prepSoCompleted;

                  return (
                    <div
                      key={`${d.name}-${idx}`}
                      className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2 hover:border-white/20 hover:bg-white/10"
                    >
                      {!isArchivalMode && (
                        <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedDocNames.has(d.name)}
                            onCheckedChange={() => toggleDocSelection(d.name)}
                            aria-label={`Select ${d.name}`}
                          />
                        </div>
                      )}
                      {!isArchivalMode && (
                        <DropdownMenu 
                          open={fileOptionsDropdownOpen[d.name] || false} 
                          onOpenChange={(open) => setFileOptionsDropdownOpen(prev => ({ ...prev, [d.name]: open }))}
                        >
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => e.stopPropagation()}
                              className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 shrink-0"
                              title="File options"
                            >
                              <Menu className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-gray-900 border-white/10 text-white" align="start">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRenameClick(d.name);
                              }}
                              className="hover:bg-white/10 cursor-pointer"
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReplaceClick(d.name);
                              }}
                              className="hover:bg-white/10 cursor-pointer"
                              disabled={loadingSignoff}
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              Replace
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <button
                        type="button"
                        className="flex-1 min-w-0 text-left text-sm text-blue-300 hover:underline cursor-pointer truncate"
                        onClick={() => handleFileClick(d.url)}
                      >
                        {d.name}
                      </button>
                      {!loadingSignoff && (
                        <>
                          {isArchivalMode ? (
                            <>
                              <div className="w-24 shrink-0 flex justify-center">
                                <StaticSignoffGroup label="Preparer" entries={signoffEntry?.prep_so || []} />
                                </div>
                              <div className="w-24 shrink-0 flex justify-center">
                                <StaticSignoffGroup label="Reviewer" entries={signoffEntry?.rev_so || []} />
                                </div>
                              <div className="w-24 shrink-0 flex justify-center">
                                <StaticSignoffGroup label="Concurrent" entries={signoffEntry?.conc_so || []} />
                                </div>
                              <div className="w-24 shrink-0 flex justify-center">
                                <StaticSignoffGroup label="EQR" entries={signoffEntry?.eqr_so || []} />
                                </div>
                              <div className="w-24 shrink-0 flex justify-center">
                                <StaticSignoffGroup label="Partner" entries={signoffEntry?.partner_so || []} />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="w-24 shrink-0 flex justify-center">
                                  <SignoffBadge label="Preparer" signoffArray={signoffEntry?.prep_so || []} isEmpty={!hasPrepSo} onSignoffClick={() => handleSignoff(d.name, 'prep_so')} disabled={!canSignoffPrep} section="prep_so" />
                                </div>
                                <div className="w-24 shrink-0 flex justify-center">
                                  <SignoffBadge label="Reviewer" signoffArray={signoffEntry?.rev_so || []} isEmpty={!hasRevSo} onSignoffClick={() => handleSignoff(d.name, 'rev_so')} disabled={!canSignoffRev} section="rev_so" />
                                </div>
                                <div className="w-24 shrink-0 flex justify-center">
                                  <SignoffBadge label="Concurrent" signoffArray={signoffEntry?.conc_so || []} isEmpty={!hasConcSo} onSignoffClick={() => handleSignoff(d.name, 'conc_so')} disabled={!canSignoffConc} section="conc_so" />
                                </div>
                                <div className="w-24 shrink-0 flex justify-center">
                                  <SignoffBadge label="EQR" signoffArray={signoffEntry?.eqr_so || []} isEmpty={!hasEqrSo} onSignoffClick={() => handleSignoff(d.name, 'eqr_so')} disabled={!canSignoffEqr} section="eqr_so" />
                                </div>
                                <div className="w-24 shrink-0 flex justify-center">
                                  <SignoffBadge label="Partner" signoffArray={signoffEntry?.partner_so || []} isEmpty={!hasPartnerSo} onSignoffClick={() => handleSignoff(d.name, 'partner_so')} disabled={!canSignoffPartner} section="partner_so" />
                                </div>
                              </>
                            )}
                          </>
                        )}
                      {!isArchivalMode && (
                        <div className="w-20 shrink-0 flex items-center justify-end gap-2">
                          {/* Assign Document Button with Dropdown */}
                          <AssignDocumentBadge
                            assignedUsers={getAssignedUsersForFile(d.name)}
                            disabled={loadingTeamMembers || teamMembers.length === 0}
                            loading={loadingAssignDocument}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 relative"
                                  disabled={loadingTeamMembers || teamMembers.length === 0 || loadingAssignDocument}
                                >
                                  <UserPlus className="h-4 w-4" />
                                  {getAssignedUsersForFile(d.name).length > 0 && (
                                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-500 rounded-full"></span>
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-gray-900 border-white/10 text-white max-h-64 overflow-y-auto">
                                {loadingTeamMembers ? (
                                  <div className="p-2 text-center text-white/60 text-sm">
                                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                                  </div>
                                ) : teamMembers.length === 0 ? (
                                  <div className="p-2 text-center text-white/60 text-sm">
                                    No team members available
                                  </div>
                                ) : (
                                  teamMembers.map((member) => (
                                    <DropdownMenuItem
                                      key={member.id}
                                      className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer"
                                      onClick={() => handleAssignDocument(d.name, member.user_name || member.email || '')}
                                      disabled={loadingAssignDocument}
                                    >
                                      <div className="flex flex-col">
                                        <span>{member.user_name || member.email}</span>
                                        {member.email && member.user_name && (
                                          <span className="text-xs text-white/60">{member.email}</span>
                                        )}
                                      </div>
                                    </DropdownMenuItem>
                                  ))
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </AssignDocumentBadge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(d.name)}
                            className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
          </div>
        </div>
      )}

      {fileToRename && (
        <RenameFileDialog
          isOpen={renameDialogOpen}
          onClose={() => {
            setRenameDialogOpen(false);
            setFileToRename(null);
          }}
          fileName={fileToRename.name}
          onRename={handleRename}
        />
      )}
    </div>
  );
}

